import type { SpeciesCategoryKey } from '@/utils/speciesCategoryUtils';

/** Activity tags aligned with host/event categories (subset of ALLOWED_HOST_CATEGORIES). */
export type SpeciesActivityTag =
  | 'foraging'
  | 'fishing'
  | 'hunting'
  | 'wildflower'
  | 'wildlife_birding'
  | 'photography';

const SPECIES_ACTIVITY_TAG_VALUES: SpeciesActivityTag[] = [
  'foraging',
  'fishing',
  'hunting',
  'wildflower',
  'wildlife_birding',
  'photography',
];

export type SpeciesActivityTagMeta = {
  value: SpeciesActivityTag;
  label: string;
  shortLabel: string;
  hostCategory: SpeciesActivityTag;
};

const SPECIES_ACTIVITY_TAGS: SpeciesActivityTagMeta[] = [
  { value: 'foraging', label: 'Foraging', shortLabel: 'Foraging', hostCategory: 'foraging' },
  { value: 'fishing', label: 'Fishing', shortLabel: 'Fishing', hostCategory: 'fishing' },
  { value: 'hunting', label: 'Hunting', shortLabel: 'Hunting', hostCategory: 'hunting' },
  {
    value: 'wildflower',
    label: 'Wildflowers',
    shortLabel: 'Wildflower',
    hostCategory: 'wildflower',
  },
  {
    value: 'wildlife_birding',
    label: 'Wildlife & birding',
    shortLabel: 'Wildlife',
    hostCategory: 'wildlife_birding',
  },
  {
    value: 'photography',
    label: 'Photography',
    shortLabel: 'Photo',
    hostCategory: 'photography',
  },
];

const metaByValue = Object.fromEntries(
  SPECIES_ACTIVITY_TAGS.map((t) => [t.value, t]),
) as Record<SpeciesActivityTag, SpeciesActivityTagMeta>;

export function isSpeciesActivityTag(value: string): value is SpeciesActivityTag {
  return SPECIES_ACTIVITY_TAG_VALUES.includes(value as SpeciesActivityTag);
}

export function getActivityTagMeta(value: SpeciesActivityTag): SpeciesActivityTagMeta {
  return metaByValue[value];
}

function resolveActivityTagLabels(
  tags: string[] | undefined | null,
): SpeciesActivityTagMeta[] {
  if (!Array.isArray(tags)) return [];
  const seen = new Set<SpeciesActivityTag>();
  const out: SpeciesActivityTagMeta[] = [];
  for (const raw of tags) {
    const key = String(raw).trim().toLowerCase();
    if (!isSpeciesActivityTag(key) || seen.has(key)) continue;
    seen.add(key);
    out.push(metaByValue[key]);
  }
  return out;
}

const FORAGING_CATEGORIES: SpeciesCategoryKey[] = [
  'mushroom',
  'fruit',
  'green',
  'nut',
  'root',
  'other',
];

type InferInput = {
  category?: string | null;
  edibility?: string | null;
  slug?: string | null;
};

/** Default activity tags when CMS/local data omits them. */
export function inferDefaultActivityTags(
  category: string | null | undefined,
  species?: InferInput,
): SpeciesActivityTag[] {
  const c = String(category || '').toLowerCase() as SpeciesCategoryKey;
  const slug = String(species?.slug || '').toLowerCase();

  if (slug === 'alectoris-chukar') {
    return ['hunting'];
  }

  if (c === 'game') {
    return ['hunting'];
  }

  if (c === 'fish') {
    return ['fishing'];
  }

  if (c === 'wildflower') {
    return ['wildflower'];
  }

  if (slug === 'acer-saccharum') {
    return ['foraging'];
  }

  if (slug === 'sambucus-canadensis') {
    return ['foraging'];
  }

  if (FORAGING_CATEGORIES.includes(c)) {
    return ['foraging'];
  }

  return ['foraging'];
}

const SPECIES_CARD_ACTIVITY_TAGS: SpeciesActivityTag[] = [
  'foraging',
  'fishing',
  'hunting',
  'wildflower',
];

/** Activity pills for the shared map + public field guide. */
export const SPECIES_LIST_FILTER_ACTIVITY_TAGS: SpeciesActivityTag[] = [
  'foraging',
  'wildflower',
];

function speciesCardActivityTags(tags: SpeciesActivityTag[]): SpeciesActivityTag[] {
  return tags.filter((t) => SPECIES_CARD_ACTIVITY_TAGS.includes(t));
}

/** Game/fish/wildflower must not inherit foraging (or other mismatched) tags from CMS. */
export function sanitizeActivityTagsForCategory(
  tags: SpeciesActivityTag[],
  category: string | null | undefined,
  species?: InferInput,
): SpeciesActivityTag[] {
  const slug = String(species?.slug || '').toLowerCase();
  const c = String(category || '').toLowerCase();
  let out = speciesCardActivityTags(tags);

  if (slug === 'alectoris-chukar' || c === 'game') {
    out = out.filter((t) => t !== 'foraging' && t !== 'fishing');
    if (!out.includes('hunting')) out.unshift('hunting');
    return [...new Set(out)];
  }

  if (c === 'fish') {
    out = out.filter((t) => t !== 'foraging' && t !== 'hunting');
    if (!out.includes('fishing')) out.unshift('fishing');
    return [...new Set(out)];
  }

  if (c === 'wildflower') {
    out = out.filter(
      (t) => t !== 'foraging' && t !== 'fishing' && t !== 'hunting',
    );
    if (!out.includes('wildflower')) out.unshift('wildflower');
    return [...new Set(out)];
  }

  if (FORAGING_CATEGORIES.includes(c as SpeciesCategoryKey)) {
    out = out.filter((t) => t !== 'fishing' && t !== 'hunting');
    if (!out.includes('foraging')) out.unshift('foraging');
    return [...new Set(out)];
  }

  return out;
}

export function resolveSpeciesActivityTags(
  tags: string[] | undefined | null,
  category: string | null | undefined,
  species?: InferInput,
): SpeciesActivityTagMeta[] {
  const explicit = sanitizeActivityTagsForCategory(
    resolveActivityTagLabels(tags).map((t) => t.value),
    category,
    species,
  );
  if (explicit.length > 0) {
    return resolveActivityTagLabels(explicit);
  }
  return resolveActivityTagLabels(
    sanitizeActivityTagsForCategory(
      inferDefaultActivityTags(category, species),
      category,
      species,
    ),
  );
}
